import React from "react";
import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { defaultConfig, defaultConfigGoodchop } from "./default_config";
import { PivotControl } from "../../constants/pivot_control_options";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { SupplyWalkEditableCustomControls } from "./custom_controls";
import { getSupplyWalkEditableViewMatrix } from "./get_matrix";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useApi, useCustomer } from "../../contexts/app/provider";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../contexts/sys_messages/type";
import {
  SaveApprovedPOsDocument,
  PoActionType,
} from "../../services/api/__generated__/backend_gateway-types";
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";
import { SupplyWalkEditableNestedDetails } from "./nested_details";

// TODO: Implement cascadeRecalculateNested helper when we have a way to modify Arrow table data

export const SupplyWalkEditableContainer = () => {
  const { apiService } = useApi();
  const { selectedCustomerId } = useCustomer();
  const { mainRun, lastSuccessfulRun } = React.useContext(VersionedDataContext);
  const { addSystemMessage } = React.useContext(SysMessagesContext);
  const supplyGuard = React.useContext(SupplyWalkEditsContext);

  // Persistent storage is now managed by the SupplyWalkEditsContext

  // Row edit state storage (temporary edits while editing)
  // This replaces the module-level globalRowEditState to properly scope state to this route
  const rowEditStateRef = React.useRef(
    new Map<
      string,
      {
        isEditing: boolean;
        editValues: Record<string, string>;
        committedValues: Record<string, string>;
        originalValues: Record<string, string>;
        listeners: Set<() => void>;
      }
    >()
  ).current;

  // Serialized save queue. saveApprovedPOs on the backend is a read-modify-write
  // against approved_pos.parquet in S3, so two concurrent saves can clobber
  // each other (each reads the pre-state, each appends its row, last writer
  // wins → earlier rows lost). We coalesce rapid clicks on the same row and
  // drain the queue sequentially so only one save is in flight at a time.
  const pendingSavesRef = React.useRef<
    Map<string, { rowData: any; checked: boolean }>
  >(new Map());
  const flushTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const isFlushingRef = React.useRef(false);

  // Cleanup debounce timer on unmount (in-flight save will complete on its own)
  React.useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  // Clear all edits when run changes to prevent cross-run contamination
  React.useEffect(() => {
    // Clear all edits when run changes
    rowEditStateRef.clear();
  }, [
    mainRun?.runSettings?.runAttemptId,
    lastSuccessfulRun?.runSettings?.runAttemptId,
    rowEditStateRef,
  ]);

  // Save approved PO action to backend (backend handles merge logic)
  const saveApprovedPOsToS3 = React.useCallback(
    async (rowData: any, isApproving: boolean) => {
      try {
        const runToUse = mainRun || lastSuccessfulRun;
        if (!runToUse) {
          return;
        }

        // Get the row key to look up edits
        const runAttemptId =
          mainRun?.runSettings?.runAttemptId ||
          lastSuccessfulRun?.runSettings?.runAttemptId ||
          "no-run";
        const rowKey = `${rowData.purchase_order_id || "unknown"}-${rowData.sku || "unknown"}-${rowData.location || "unknown"}-${rowData.supplier || "unknown"}-${runAttemptId}`;
        const editData = supplyGuard?.getEdit?.(rowKey);

        // Extract edited values if they exist
        const editedValues = editData?.committedValues || {};

        // GoodChop "Existing PO's to Modify" flow: when pallets was bumped,
        // derive quantity = newPallets * UPP and total_cost = quantity *
        // unit_cost so the saved values stay consistent with what's on
        // screen. The checkbox cell holds a stale rowData reference (its
        // quantity/total_cost weren't mutated by handleEdit since they're
        // not in editableColumns on this tab), so we can't read them off
        // rowData directly. Returns null when pallets wasn't bumped — the
        // existing fallbacks below handle every non-pallet-edit path.
        const palletBumpDerived: { quantity: number; total_cost: number } | null = (() => {
          const originalRaw = editData?.originalValues?.pallets;
          const originalPallets =
            originalRaw != null && originalRaw !== ""
              ? Number(originalRaw)
              : (rowData.pallets != null ? Number(rowData.pallets) : null);
          if (originalPallets == null || !(originalPallets > 0)) return null;
          const editedRaw = editedValues.pallets;
          if (editedRaw == null || editedRaw === "") return null;
          const parsedNew = Math.floor(
            Number(String(editedRaw).replace(/,/g, ""))
          );
          if (!Number.isFinite(parsedNew)) return null;
          const newPallets =
            parsedNew < originalPallets ? originalPallets : parsedNew;
          if (newPallets === originalPallets) return null;
          // Use __originalQuantity (stashed by getRow before its override) so
          // UPP stays stable across multiple commits — rowData.quantity could
          // already be a derived value from a prior pallets bump.
          const originalQuantity = Number(
            rowData.__originalQuantity ?? rowData.quantity ?? 0
          );
          const upp = originalQuantity / originalPallets;
          if (!Number.isFinite(upp) || !(upp > 0)) return null;
          const newQuantity = Math.round(newPallets * upp);
          const unitCost = editedValues.unit_cost
            ? parseFloat(editedValues.unit_cost)
            : Number(rowData.unit_cost ?? 0);
          const newTotalCost = Number.isFinite(unitCost)
            ? newQuantity * unitCost
            : Number(rowData.total_cost ?? 0);
          return { quantity: newQuantity, total_cost: newTotalCost };
        })();

        // Send individual PO action to backend
        await apiService.getClient().mutate({
          mutation: SaveApprovedPOsDocument,
          variables: {
            runSettings: {
              inputId: runToUse.runSettings.inputId,
              workflowId: runToUse.runSettings.workflowId,
              settingsNameAndVersion:
                runToUse.runSettings.settingsNameAndVersion,
              runAttemptId: runToUse.runSettings.runAttemptId,
            },
            poAction: {
              purchase_order_id: rowData.purchase_order_id,
              sku: rowData.sku,
              sku_name: rowData.sku_name,
              location: rowData.location,
              supplier: rowData.supplier,
              // Use edited values if they exist, otherwise use original values.
              // GoodChop pallet-bump derivation (`palletBumpDerived`, computed
              // below) overrides quantity / total_cost when present — for all
              // other flows the existing fallbacks apply unchanged.
              quantity:
                palletBumpDerived?.quantity ??
                (editedValues.quantity
                  ? parseFloat(editedValues.quantity)
                  : rowData.quantity),
              total_cost:
                palletBumpDerived?.total_cost ??
                (editedValues.total_cost
                  ? parseFloat(editedValues.total_cost)
                  : rowData.total_cost),
              unit_cost: editedValues.unit_cost
                ? parseFloat(editedValues.unit_cost)
                : rowData.unit_cost,
              order_placed_date:
                editedValues.order_placed_date || rowData.order_placed_date,
              order_delivery_date:
                editedValues.order_delivery_date || rowData.order_delivery_date,
              // Pallets is only present on Open PO Modifications rows. The
              // GoodChop modify-existing flow uses pallet count + UPP-derived
              // quantity; persist both so downstream can audit either. Edits
              // are integer + additive-only — clamp to >= recommended pallets.
              // Floor comes from editData.originalValues (rowData.pallets is
              // already post-merge so it can't be the floor).
              pallets: (() => {
                const originalRaw = editData?.originalValues?.pallets;
                const original =
                  originalRaw != null && originalRaw !== ""
                    ? Number(originalRaw)
                    : (rowData.pallets ?? null);
                const editedRaw = editedValues.pallets;
                if (editedRaw == null || editedRaw === "") {
                  return original;
                }
                const parsed = Math.floor(
                  Number(String(editedRaw).replace(/,/g, ""))
                );
                if (!Number.isFinite(parsed)) return original;
                if (original != null && parsed < original) return original;
                return parsed;
              })(),
              action: isApproving
                ? PoActionType.Approve
                : PoActionType.Unapprove,
            },
          },
        });
      } catch (error) {
        throw error; // Re-throw to handle in calling function
      }
    },
    [mainRun, lastSuccessfulRun, apiService, supplyGuard]
  );

  // Drain the pending-saves queue one at a time. New clicks arriving during
  // a drain are picked up on the next iteration, so any burst ends with a
  // single bumpReloadVersion instead of one per save.
  const flushPendingSaves = React.useCallback(async () => {
    if (isFlushingRef.current) return;
    isFlushingRef.current = true;
    let didSave = false;
    try {
      while (pendingSavesRef.current.size > 0) {
        const entry = pendingSavesRef.current.entries().next().value;
        if (!entry) break;
        const [rowKey, payload] = entry;
        pendingSavesRef.current.delete(rowKey);
        didSave = true;
        try {
          await saveApprovedPOsToS3(payload.rowData, payload.checked);
        } catch (error) {
          const errorMsg =
            (error as Error)?.message || String(error) || "Unknown error";
          addSystemMessage({
            level: SysMessageLevel.DANGER,
            message: `Failed to save approved POs: ${errorMsg}`,
          });
        }
      }
    } finally {
      isFlushingRef.current = false;
    }
    if (didSave) {
      supplyGuard?.bumpReloadVersion?.();
    }
  }, [saveApprovedPOsToS3, addSystemMessage, supplyGuard]);

  // Handle checkbox toggle: apply optimistic state, then enqueue a save.
  const handleApprovedToggle = React.useCallback(
    (rowKey: string, rowData: any, checked: boolean) => {
      // Update the edit context immediately for navigation guard
      if (supplyGuard?.setRowApproved) {
        supplyGuard.setRowApproved(rowKey, checked);
      }

      // Update approved row count in real-time
      if (checked) {
        supplyGuard?.incrementApprovedRowCount?.();
        // Record the source bucket so the status-bar tab counts decrement
        // immediately on click. The DUMMY-row counts baked into the parquet
        // only update after save → server cache invalidate → re-subscribe →
        // fresh transform — a multi-second round trip the user shouldn't see.
        // Skip when source is already "Approved" (unapprove-then-approve on
        // the Approved tab) since there's no source bucket to decrement.
        const sourceStatus = String(rowData?.status ?? "");
        if (sourceStatus && sourceStatus !== "Approved") {
          supplyGuard?.recordPendingApproval?.(rowKey, sourceStatus);
        }
      } else {
        supplyGuard?.decrementApprovedRowCount?.();
        // Drop the optimistic delta if we created one this session. We can't
        // restore the source bucket count for a row that was already approved
        // before page load (the original status isn't tracked) — those refresh
        // on the next data update.
        supplyGuard?.clearPendingApproval?.(rowKey);
      }

      // Last click wins for a given row within the debounce window.
      pendingSavesRef.current.set(rowKey, { rowData, checked });

      // Debounce: schedule a flush 1s after the most recent click. If a
      // flush is already running, its loop will pick up the new entry on
      // the next iteration; this timer is a backup trigger for the case
      // where the running flush has already exited.
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        void flushPendingSaves();
      }, 1000);
    },
    [supplyGuard, flushPendingSaves]
  );

  const CustomControlsWithApprovedCount = React.useCallback((props: any) => {
    return <SupplyWalkEditableCustomControls {...props} />;
  }, []);

  const isGoodchop = selectedCustomerId === "goodchop";

  return (
    <TransformedCustomViewContainer
      viewControllerConfig={
        {
          datasetName: "po_plan_edit",
          transformId: isGoodchop
            ? "supply_walk_editable_goodchop"
            : "supply_walk_editable",
          folder: "outputs",
          title: "Purchase Order Inbox (Beta)",
          dateHierarchyColumnFilter: null,
          excludedSegmentationsFilter: [],
          measureColumnFilter: null,
          excludedFilterColumnFilter: [],
          columnHeaderMap: isGoodchop
            ? {
                purchase_order_id: "PO Number",
                sku: "SKU Code",
                sku_name: "Description",
                quantity: "Order Quantity",
                order_placed_date: "Purchase Order Date",
                order_delivery_date: "ETA to Warehouse",
                unit_cost: "Price per Unit",
                total_cost: "Total Cost",
                status: "Purchase Order Status",
                pallets: "Pallets",
                modification_reason: "Modification Reason",
                targeted_po_arrival: "Targeted PO Arrival",
                targeted_po_placement: "Targeted PO Placement",
              }
            : {
                purchase_order_id: "PO ID",
                sku: "SKU",
                sku_name: "SKU name",
                order_placed_date: "PO day",
                order_delivery_date: "ETA day",
                total_cost: "total cost",
                unit_cost: "unit cost",
              },
          showControls: [
            PivotControl.Download,
            PivotControl.ShowSettings,
            PivotControl.StoreAsDefault,
          ],
          customControlPanel: CustomControlsWithApprovedCount,
          paramsToTransformArgs: (
            params: DatePivotParams
          ): Record<string, any> => {
            return {
              date_field: params.dateHierarchy,
              group_by_fields: [],
              filters: params.filters,
            };
          },
          tableType: "flat",
          getMatrix: (params: any) => {
            // Try multiple ways to get the current status
            let currentStatus = "";

            // Method 1: Check params.filters
            if (params.filters) {
              const statusFilter = params.filters.find(
                (filter: any) => filter[0] === "status"
              );
              currentStatus = statusFilter ? statusFilter[2][0] : "";
            }

            // Method 2: Check if status is directly in params
            if (!currentStatus && params.status) {
              currentStatus = params.status;
            }

            // Method 3: Check viewParams.filters
            if (!currentStatus && params.viewParams?.filters) {
              const statusFilter = params.viewParams.filters.find(
                (filter: any) => filter[0] === "status"
              );
              currentStatus = statusFilter ? statusFilter[2][0] : "";
            }

            return getSupplyWalkEditableViewMatrix({
              ...params,
              viewParams: { ...(params?.viewParams || {}) },
              mainRun,
              lastSuccessfulRun,
              NestedDetailsComponent: SupplyWalkEditableNestedDetails,
              currentStatus,
              rowEditStateRef,
              onApprovedToggle: handleApprovedToggle,
              supplyGuard,
            });
          },
        } as any
      }
      defaultConfig={isGoodchop ? defaultConfigGoodchop : defaultConfig}
    />
  );
};
